/**
 * TYPING ONE IN, after a call or a conversation. Every channel but the website's, which arrives on
 * its own.
 */
import { useState, type FormEvent } from 'react';
import { SERVICE_LINES, SERVICE_LINE_LABELS, TYPED_CHANNELS, type ServiceLine } from '@fk/shared';
import { ApiError, createEnquiry } from '../../api.ts';
import { Button, SelectField, TextAreaField, TextField } from '../../components/Field.tsx';
import { ErrorNotice } from '../../components/Notice.tsx';
import { SourceFields } from '../../components/SourceFields.tsx';
import { CheckField } from '../../components/VisitBits.tsx';
import { E164, orNull, toE164 } from '../../format.ts';
import type { SourceAnswer } from '../../source.ts';
import { EMAIL } from './rows.ts';

interface NewDraft {
  /**
   * THE SAME BLOCK AS THE OTHER THREE, and it is this form that most needs it. A fresh
   * `source_detail` is BORN here, so a form with no picker is the one place three spellings of one
   * Nextdoor thread get invented, and report A groups on that column. It is also the door a
   * referral comes in through: without the introducer beside the word, a household reached the
   * client book credited to nobody and about $107 of budgeted credit had to be reattached
   * afterwards through the correction block.
   */
  source: SourceAnswer;
  name: string;
  phone: string;
  email: string;
  town: string;
  zip: string;
  serviceLine: ServiceLine | '';
  notes: string;
  responded: boolean;
}

const BLANK: NewDraft = {
  source: { sourceChannel: '', sourceDetail: '', referredByClientId: null },
  name: '',
  phone: '',
  email: '',
  town: '',
  zip: '',
  serviceLine: '',
  notes: '',
  responded: false,
};

export function TypeOneIn({
  onSaved,
  onCancel,
}: {
  onSaved: (sentence: string) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<NewDraft>(BLANK);
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [saving, setSaving] = useState(false);

  const set = <K extends keyof NewDraft>(field: K, value: NewDraft[K]) =>
    setDraft((previous) => ({ ...previous, [field]: value }));

  async function submit(event: FormEvent) {
    event.preventDefault();
    setFailure(null);
    const found: string[] = [];
    // `website_form` is not in `TYPED_CHANNELS`, so it cannot be chosen here; the word is named
    // anyway because the type has to be narrowed to what the door accepts, and the server refuses
    // it in this same sentence if a request ever arrives carrying it.
    const channel = draft.source.sourceChannel;
    const typed = channel !== '' && channel !== 'website_form';
    if (!typed) {
      found.push('Say how it came in. A website lead arrives on its own and is never typed in.');
    }
    if (!draft.name.trim()) found.push('The person who asked needs a name.');
    if (draft.phone.trim() && !E164.test(toE164(draft.phone) ?? '')) {
      found.push('That phone number does not look right. Ten digits is enough, like 727 337 7775.');
    }
    if (draft.email.trim() && !EMAIL.test(draft.email.trim())) {
      found.push('That email address does not look right.');
    }
    setProblems(found);
    if (found.length > 0 || !typed) return;

    setSaving(true);
    try {
      await createEnquiry({
        sourceChannel: channel,
        sourceDetail: orNull(draft.source.sourceDetail),
        // The credit follows the word here too: an introducer chosen and then a different channel
        // chosen over it is a payment nobody could explain, so only a referral carries one.
        referredByClientId: channel === 'referral' ? draft.source.referredByClientId : null,
        name: draft.name.trim(),
        phoneE164: draft.phone.trim() ? toE164(draft.phone) : null,
        email: orNull(draft.email),
        town: orNull(draft.town),
        zip: orNull(draft.zip),
        serviceLine: draft.serviceLine ? draft.serviceLine : null,
        notes: orNull(draft.notes),
        responded: draft.responded,
      });
      setDraft(BLANK);
      onSaved(
        draft.responded
          ? 'Saved as contacted, with the reply on the record. It is yours to work.'
          : 'Saved as new. It is yours to work, and the same-day clock is running.',
      );
    } catch (thrown) {
      setFailure(
        thrown instanceof ApiError
          ? thrown
          : new ApiError(0, 'error', 'That enquiry was not saved.'),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <h2 className="font-serif text-lg">Type one in</h2>
      <p className="mt-1 mb-3 text-sm text-navy/70">
        For what came by phone, message, referral or in person. A website lead arrives here on its
        own.
      </p>

      {failure ? (
        <div className="mb-4">
          <ErrorNotice title="Nothing was saved" message={failure.message} />
        </div>
      ) : null}

      {problems.length > 0 ? (
        <div
          className="mb-4 rounded-xl border border-coral/50 border-l-4 border-l-coral bg-parchment p-4"
          role="alert"
        >
          <h3 className="font-serif text-lg">Before this can be saved</h3>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {/*
       * Full width and above the rest, rather than a cell in the grid below: the introducer picker
       * opens into a search box and a list of households under it, and a picker crushed into half
       * a column is one nobody uses.
       */}
      <SourceFields
        idPrefix="type-one-in"
        channels={TYPED_CHANNELS}
        required
        value={draft.source}
        onChange={(source) => set('source', source)}
        channelLabel="How it came in"
        channelPlaceholder="Choose one"
        channelHint="Never guessed. With a handful of clients this is the only honest read on which channel is worth money."
        detailHint="Which Nextdoor thread, which realtor's office, which letter batch. Choose one already typed where there is one, so the roll-up counts them together."
      />

      <div className="md:grid md:grid-cols-2 md:gap-4">
        <TextField
          id="enquiry-name"
          label="Name"
          required
          value={draft.name}
          onChange={(value) => set('name', value)}
          autoComplete="off"
        />
        <TextField
          id="enquiry-phone"
          label="Phone"
          type="tel"
          inputMode="tel"
          value={draft.phone}
          onChange={(value) => set('phone', value)}
          hint="Ten digits is enough. The phone system knows a caller by this number."
          autoComplete="off"
        />
        <TextField
          id="enquiry-email"
          label="Email"
          type="email"
          inputMode="email"
          value={draft.email}
          onChange={(value) => set('email', value)}
          autoComplete="off"
        />
        <SelectField
          id="enquiry-service-line"
          label="What they asked about"
          value={draft.serviceLine}
          onChange={(value) => set('serviceLine', value)}
          options={SERVICE_LINES}
          toLabel={(option) => SERVICE_LINE_LABELS[option]}
          placeholder="Not said yet"
        />
      </div>
      <div className="grid grid-cols-2 gap-3 md:w-1/2 md:pr-2">
        <TextField
          id="enquiry-town"
          label="Town"
          value={draft.town}
          onChange={(value) => set('town', value)}
          autoComplete="off"
        />
        <TextField
          id="enquiry-zip"
          label="ZIP"
          inputMode="numeric"
          value={draft.zip}
          onChange={(value) => set('zip', value)}
          autoComplete="off"
        />
      </div>
      <TextAreaField
        id="enquiry-notes"
        label="Notes"
        value={draft.notes}
        onChange={(value) => set('notes', value)}
        hint="What they said, in their words. Beds and baths if they mentioned them."
      />
      <CheckField
        id="enquiry-responded"
        label="I already replied"
        checked={draft.responded}
        onChange={(value) => set('responded', value)}
        hint="Ticked, it starts as contacted with the reply on the record now. Unticked, it is new and the same-day clock is running."
      />
      <div className="flex flex-col gap-3 md:flex-row">
        <Button type="submit" full disabled={saving}>
          {saving ? 'Saving' : 'Save this enquiry'}
        </Button>
        <Button tone="quiet" onClick={onCancel} disabled={saving}>
          Not now
        </Button>
      </div>
    </form>
  );
}
